import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const EmbedCodeGenerator = ({ baseUrl, companyIdentifier, companyName }) => {
  const [width, setWidth] = useState('100%');
  const [height, setHeight] = useState('800px');
  const [showBranding, setShowBranding] = useState(true);
  const [responsive, setResponsive] = useState(true);
  const [copied, setCopied] = useState(false);

  const generateEmbedCode = () => {
    const params = new URLSearchParams();
    if (!showBranding) params?.append('hideBranding', 'true');
    
    const iframeUrl = `${baseUrl}${params?.toString() ? '?' + params?.toString() : ''}`;
    
    if (responsive) {
      return `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden;">
  <iframe 
    src="${iframeUrl}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
    title="Epoxy Flooring Visualizer"
    loading="lazy"
    allowfullscreen>
  </iframe>
</div>`;
    }
    
    return `<iframe 
  src="${iframeUrl}"
  width="${width}"
  height="${height}"
  style="border: none;"
  title="Epoxy Flooring Visualizer"
  loading="lazy"
  allowfullscreen>
</iframe>`;
  };

  const embedCode = generateEmbedCode();

  const handleCopy = () => {
    navigator.clipboard?.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="Code" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-text-primary">Embed Code Generator</h3>
      </div>

      {/* Company Verification Badge */}
      {companyIdentifier && (
        <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Building2" size={18} className="text-primary mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-text-primary mb-1">
                Company-Specific Embed Code
              </p>
              <p className="text-xs text-text-secondary">
                {companyName ? (
                  <>This embed code is configured for: <span className="font-semibold text-primary">{companyName}</span></>
                ) : (
                  <>Company ID: <span className="font-mono text-primary">{companyIdentifier}</span></>
                )}
              </p>
              <p className="text-xs text-text-secondary mt-2">
                ✓ All visualizer data and branding will be specific to this company
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Input
          type="text"
          label="Width"
          value={width}
          onChange={(e) => setWidth(e?.target?.value)}
          placeholder="100% or 800px"
          disabled={responsive}
        />
        <Input
          type="text"
          label="Height"
          value={height}
          onChange={(e) => setHeight(e?.target?.value)}
          placeholder="600px"
          disabled={responsive}
        />
      </div>
      <div className="space-y-3 mb-6">
        <Checkbox
          label="Responsive (16:9 aspect ratio)"
          description="Automatically adjusts to container width"
          checked={responsive}
          onChange={(e) => setResponsive(e?.target?.checked)}
        />
        <Checkbox
          label="Show company branding"
          description="Display company logo and name in visualizer"
          checked={showBranding}
          onChange={(e) => setShowBranding(e?.target?.checked)}
        />
      </div>
      <div className="bg-muted border border-border rounded-lg p-4 mb-4">
        {companyIdentifier && (
          <div className="mb-3 pb-3 border-b border-border">
            <div className="flex items-center gap-2 text-xs">
              <Icon name="CheckCircle" size={14} className="text-success" />
              <span className="text-text-secondary">
                Company URL verified: 
                <span className="ml-1 font-mono text-primary">/client/{companyIdentifier}/embed</span>
              </span>
            </div>
          </div>
        )}
        <pre className="text-xs text-text-primary overflow-x-auto whitespace-pre-wrap break-all font-mono">
          {embedCode}
        </pre>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant={copied ? 'success' : 'default'}
          size="sm"
          fullWidth
          onClick={handleCopy}
          iconName={copied ? 'Check' : 'Copy'}
          iconPosition="left"
          iconSize={14}
        >
          {copied ? 'Copied to Clipboard!' : 'Copy Embed Code'}
        </Button>
      </div>
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
          <div className="text-xs text-text-secondary">
            <p className="font-medium text-text-primary mb-1">Integration Instructions:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Copy the embed code above</li>
              <li>Paste it into your website's HTML where you want the visualizer to appear</li>
              <li>The visualizer will automatically load and be ready for customer use</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmbedCodeGenerator;